/**
 * QuikSheets — "business spreadsheets, without another subscription" page.
 * All copy lives here; sections only handle layout and motion.
 */

import { LINKS } from "./quikit";

export const QSH = {
  meta: {
    title: "Online spreadsheets & business data — QuikSheets by Quikit",
    description:
      "Create spreadsheets, organize business data, run calculations, build visualizations and collaborate with your team — all inside the Quikit ecosystem.",
  },
  links: { register: LINKS.register },
};

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */
export const QSH_HERO = {
  eyebrow: "Business spreadsheets, without another subscription",
  title: "Calculate. Analyze. Collaborate.\nAll in one place.",
  sub: "Create spreadsheets, organize business data, run calculations, build visualizations and collaborate with your team — all inside the Quikit ecosystem.",
  lead: "Included with any Quikit product at no additional cost.",
  foot: "No credit card required. Up and running in minutes.",
  link: { label: "See QuikSheets in action", href: "#how" },
};

/** The sheet mock shown in the hero. */
export const QSH_PULSE = {
  path: "Workspace → Sheets → Q3 pipeline",
  sheet: { name: "Q3 pipeline tracker", meta: "shared · 4 editors · linked to QuikCRM" },
  collaborators: [
    { name: "Nikhil", state: "Editing" as const },
    { name: "Fatima", state: "Commenting" as const },
    { name: "Arjun", state: "Viewing" as const },
  ],
  columns: ["Region", "Pipeline", "Closed", "Win rate"],
  rows: [
    { cells: ["North", "$42,000", "$18,400", "44%"], state: "up" as const },
    { cells: ["West", "$38,500", "$12,100", "31%"], state: "up" as const },
    { cells: ["South", "$27,200", "$6,000", "22%"], state: "down" as const },
    { cells: ["East", "$19,800", "$9,300", "47%"], state: "up" as const },
  ],
  formula: "=SUM(C2:C5)/SUM(B2:B5)",
  result: { label: "Blended win rate", value: "36.4%" },
  footer: "South is dragging the blended rate — 22% against a 36.4% company average.",
};

/* ------------------------------------------------------------------ */
/*  The problem                                                        */
/* ------------------------------------------------------------------ */
export const QSH_PROBLEM = {
  eyebrow: "The problem",
  title: "Your business runs on data. But your data doesn’t always stay connected.",
  text: "Sales trackers, budgets, project plans, forecasts — spreadsheets are everywhere. Kept apart from your systems, they create more work than they save.",
  items: [
    {
      title: "Too many versions",
      text: "Different teams maintain their own copies of the same spreadsheet, making it hard to know which numbers are current.",
    },
    {
      title: "Data gets scattered",
      text: "Important business information ends up across spreadsheets, drives, emails and different applications.",
    },
    {
      title: "Manual work adds up",
      text: "Copying data, updating formulas, reconciling sheets and preparing reports consume hours that should go into analysis.",
    },
    {
      title: "Collaboration gets messy",
      text: "When multiple people work on different versions, small changes quickly create bigger inconsistencies.",
    },
    {
      title: "Data doesn’t have context",
      text: "A spreadsheet holds the number — but not the project, customer, employee or workflow behind it.",
    },
    {
      title: "Too many tools",
      text: "Businesses pay separately for spreadsheets, reporting tools, dashboards and collaboration software.",
    },
  ],
  result: {
    kicker: "The real problem",
    title: "The problem isn’t spreadsheets. It’s disconnected data.",
    text: "A number is only useful when you can see the work that produced it.",
    cta: "See how QuikSheets works",
  },
};

/* ------------------------------------------------------------------ */
/*  The shift                                                          */
/* ------------------------------------------------------------------ */
export const QSH_SHIFT = {
  eyebrow: "The shift",
  title: "Stop treating spreadsheets as files. Start using them as part of your business.",
  text: "Spreadsheet work runs export, edit, email, reconcile, upload, repeat. QuikSheets brings it into one place.",
  /** Each row is a genuine swap — no term appears on both sides of the arrow. */
  rows: [
    { from: "Export", to: "Create" },
    { from: "Download", to: "Calculate" },
    { from: "Email around", to: "Analyze" },
    { from: "Reconcile copies", to: "Collaborate" },
    { from: "Upload again", to: "Connect" },
  ],
  questionsLead: "Instead of asking “where is the latest spreadsheet?”, your team can focus on the questions the data is actually for:",
  questions: ["What does the data tell us?", "What changed?", "Why did it change?", "What should we do next?"],
};

/* ------------------------------------------------------------------ */
/*  How it works                                                       */
/* ------------------------------------------------------------------ */
export const QSH_STEPS = {
  eyebrow: "Meet QuikSheets",
  // Too long for the column on one line, so the break is placed rather than left to chance.
  title: "Your everyday spreadsheet workspace,\nbuilt into Quikit.",
  text: "A familiar way for every team to create, manage, analyze and collaborate on business data — without adding another disconnected tool to your stack.",
  steps: [
    {
      kicker: "Create",
      title: "A familiar, flexible workspace",
      text: "Build spreadsheets in an environment your team already knows how to use, with no new way of working to learn first.",
    },
    {
      kicker: "Calculate",
      title: "Let the spreadsheet do the maths",
      text: "Use formulas and functions to work with numbers faster and automate the repetitive calculations that eat the afternoon.",
    },
    {
      kicker: "Analyze",
      title: "Rows and columns into information",
      text: "Turn raw data into something useful through tables, charts and structured analysis rather than manual inspection.",
    },
    {
      kicker: "Collaborate",
      title: "One sheet instead of five copies",
      text: "Work together on the same data instead of passing multiple spreadsheet versions around and reconciling them later.",
    },
    {
      kicker: "Organize",
      title: "Business data that stays findable",
      text: "Keep business data structured, accessible and easier to manage in one central workspace.",
    },
    {
      kicker: "Connect",
      title: "Closer to the work that made it",
      text: "Bring spreadsheet work closer to the Quikit applications and workflows your teams already use.",
    },
  ],
  cta: "Start creating for free",
};

/* ------------------------------------------------------------------ */
/*  Everything included                                                */
/* ------------------------------------------------------------------ */
export const QSH_EVERYTHING = {
  eyebrow: "Everything included",
  title: "Everything you need to work with data.",
  text: "The essential capabilities to create, manage, analyze and share business data — all included with your Quikit product.",
  items: [
    { title: "Familiar interface", text: "Enter, organize and manage business data in a spreadsheet experience your team already knows." },
    { title: "Formulas & functions", text: "Perform calculations, automate repetitive work and turn raw numbers into useful outputs." },
    { title: "Tables & organization", text: "Organize large datasets into clean, structured tables that are easier to understand." },
    { title: "Charts & visualization", text: "Turn spreadsheet data into visuals that make trends, comparisons and patterns obvious." },
    { title: "Collaboration", text: "Bring your team into the same spreadsheet instead of managing multiple copies." },
    { title: "Comments & feedback", text: "Review numbers and ask questions without moving the conversation to another application." },
    { title: "Templates", text: "Create reusable spreadsheet structures for recurring business needs." },
    { title: "Data management", text: "Create, manage and organize spreadsheets in one central workspace." },
  ],
  close: "One spreadsheet workspace. More connected business data.",
};

/* ------------------------------------------------------------------ */
/*  From data to insight                                               */
/* ------------------------------------------------------------------ */
export const QSH_INSIGHT = {
  eyebrow: "From data to insights",
  title: "A spreadsheet should do more than store numbers.",
  text: "Your data becomes valuable when your team can understand it. QuikSheets moves the work from raw data through to something you can act on.",
  steps: [
    { name: "Organize", text: "Bring scattered information into structured tables." },
    { name: "Calculate", text: "Use formulas and functions to process your data." },
    { name: "Compare", text: "Identify differences, trends and patterns." },
    { name: "Visualize", text: "Turn important numbers into charts and reports." },
    { name: "Understand", text: "Give your team a clearer view of what the data means." },
  ],
  close: "Don’t just manage numbers. Make them useful.",
};

/* ------------------------------------------------------------------ */
/*  Built for every team                                               */
/* ------------------------------------------------------------------ */
export const QSH_TEAMS = {
  eyebrow: "Built for every team",
  title: "Everyone uses spreadsheets.\nEveryone gets QuikSheets.",
  text: "Spreadsheets aren’t a finance-only tool — almost every team in a business lives in them.",
  groups: [
    { name: "Finance", items: ["Budgets", "Forecasts", "Expense tracking", "Financial analysis"] },
    { name: "Sales", items: ["Lead trackers", "Sales reports", "Pipeline data", "Revenue analysis"] },
    { name: "Marketing", items: ["Campaign trackers", "Marketing reports", "Content calendars", "Performance data"] },
    { name: "HR", items: ["Employee records", "Hiring trackers", "Attendance data", "Workforce planning"] },
    { name: "Operations", items: ["Process trackers", "Inventory sheets", "Operational reports", "Resource planning"] },
    { name: "Projects", items: ["Project trackers", "Task lists", "Timelines", "Delivery reporting"] },
    { name: "Leadership", items: ["Business plans", "Dashboards", "Forecasts", "Decision support"] },
    { name: "Customer success", items: ["Customer trackers", "Account reports", "Renewal data", "Service metrics"] },
  ],
  close: "One spreadsheet workspace. Every team.",
};

/* ------------------------------------------------------------------ */
/*  Collaboration, templates and data management                       */
/* ------------------------------------------------------------------ */
export const QSH_WORK = {
  eyebrow: "Collaboration, templates & data",
  title: "One sheet. One team. One version of the data.",
  text: "Spreadsheets get hard when everyone has their own copy, starts from blank and nobody knows where things belong.",
  blocks: [
    {
      kicker: "Collaboration",
      title: "Stop reconciling versions",
      text: "Build, review and edit together, with feedback in context, so changes don’t turn into disconnected copies that have to be merged by hand later.",
      items: ["Create together", "Review together", "Edit together", "Share easily", "Stay current"],
    },
    {
      kicker: "Templates",
      title: "Don’t build the same spreadsheet twice",
      text: "Most business spreadsheets follow repeatable patterns, so there’s no reason to start from a blank sheet every time. Create once, reuse everywhere.",
      items: ["Budgets", "Sales trackers", "Project trackers", "Employee records", "Marketing reports", "Expense sheets", "Inventory trackers", "Dashboards"],
    },
    {
      kicker: "Data management",
      title: "Keep business data organized",
      text: "A spreadsheet becomes hard to use when nobody knows where it belongs. One central workspace to create, organize and maintain them.",
      items: ["Centralize", "Structure", "Maintain", "Access", "Collaborate"],
    },
  ],
  close: "Less searching. Less duplication. More usable data.",
};

/* ------------------------------------------------------------------ */
/*  Connected data                                                     */
/* ------------------------------------------------------------------ */
export const QSH_ECOSYSTEM = {
  eyebrow: "Connected business data",
  title: "Your spreadsheet knows the numbers. Your business knows the context.",
  text: "A sales spreadsheet should relate to sales. A project tracker should relate to projects. Data doesn’t have to travel alone.",
  groups: [
    { name: "QuikCRM", items: ["Customer data", "Sales analysis", "Pipeline reporting"] },
    { name: "QuikProjects", items: ["Project tracking", "Delivery reporting", "Project data"] },
    { name: "QuikPeople", items: ["Employee information", "Workforce planning", "People data"] },
    { name: "QuikFinance", items: ["Financial data", "Budgets & forecasts", "Business analysis"] },
    { name: "QuikInsights", items: ["Broader business insight", "Performance reporting", "Shared metrics"] },
    { name: "QuikScale", items: ["Planning", "Forecasting", "Strategic analysis"] },
    { name: "QuikSocial & QuikSEO", items: ["Marketing data", "Growth analysis", "Campaign performance"] },
    { name: "QuikDocs", items: ["Reports beside the data", "Shared knowledge", "Supporting documentation"] },
  ],
  close: "Your data shouldn’t live in isolation from the business it represents.",
};

/* ------------------------------------------------------------------ */
/*  Why QuikSheets                                                     */
/* ------------------------------------------------------------------ */
export const QSH_WHY = {
  eyebrow: "Why QuikSheets",
  title: "Spreadsheets are easy. Connecting them to your business is the difference.",
  text: "Less time managing spreadsheets. More time using the data.",
  items: [
    { title: "Familiar by design", text: "Your team doesn’t need to learn an entirely new way to work with data." },
    { title: "Everything you need to calculate", text: "Formulas, functions, tables and formatting for working with data efficiently." },
    { title: "Collaboration in context", text: "Build, edit, review and share without moving the work between tools." },
    { title: "Data stays organized", text: "Keep working spreadsheets structured, accessible and easier to maintain." },
    { title: "Less duplication", text: "Reduce the multiple copies of the same spreadsheet that drift apart." },
    { title: "Less manual work", text: "Formulas, templates and structured workflows accelerate the repetitive parts." },
    { title: "Connected to your business", text: "Work alongside the Quikit applications your teams already use." },
    { title: "No extra subscription", text: "QuikSheets is included with any Quikit product at no additional cost." },
  ],
};

/* ------------------------------------------------------------------ */
/*  Compare                                                            */
/* ------------------------------------------------------------------ */
export type CompareCell = boolean | string;
export const QSH_COMPARE = {
  eyebrow: "QuikSheets vs traditional spreadsheet tools",
  title: "You don’t need another spreadsheet subscription.",
  text: "The grid and formulas are table stakes. What differs is whether the sheet knows your business.",
  columns: ["QuikSheets", "Traditional tools"],
  rows: [
    { feature: "Spreadsheet creation", cells: [true, true] as CompareCell[] },
    { feature: "Formulas & functions", cells: [true, true] as CompareCell[] },
    { feature: "Tables", cells: [true, true] as CompareCell[] },
    { feature: "Data organization", cells: [true, true] as CompareCell[] },
    { feature: "Charts & visualization", cells: [true, true] as CompareCell[] },
    { feature: "Collaboration", cells: [true, true] as CompareCell[] },
    { feature: "Spreadsheet sharing", cells: [true, true] as CompareCell[] },
    { feature: "Templates", cells: [true, true] as CompareCell[] },
    { feature: "Business data management", cells: [true, true] as CompareCell[] },
    { feature: "Connected business ecosystem", cells: [true, "Limited"] as CompareCell[] },
    { feature: "Connected applications", cells: [true, "Limited"] as CompareCell[] },
    { feature: "Quikit ecosystem", cells: [true, false] as CompareCell[] },
    { feature: "Separate subscription", cells: [false, "Usually"] as CompareCell[] },
    { feature: "Included with Quikit", cells: [true, false] as CompareCell[] },
  ],
  close: {
    title: "Two ways to work with your data.",
    options: [{ name: "A traditional spreadsheet tool", text: "Helps you work with data — in its own place, on its own subscription, away from the business." }],
    product: { name: "QuikSheets", text: "Helps you work with data inside your business ecosystem, included with your Quikit product." },
  },
};

/* ------------------------------------------------------------------ */
/*  The business case                                                  */
/* ------------------------------------------------------------------ */
export const QSH_CASE = {
  eyebrow: "The business case",
  title: "Stop paying separately\nfor every tool your team needs.",
  text: "You already pay for software running customers, projects and finance. Why analyze the data elsewhere?",
  manual: {
    kicker: "A separate spreadsheet subscription",
    title: "Another per-user licence, away from the work",
    items: ["Its own bill", "Its own admin", "Its own identity", "Exported copies", "No business context", "Another place to look"],
    value: "Separate",
    unit: "per-user subscription",
  },
  product: {
    kicker: "QuikSheets",
    title: "Included with any Quikit product",
    text: "Spreadsheet creation, calculations, charts, collaboration, templates and connections with the rest of Quikit are built into the ecosystem rather than sold alongside it.",
    close: "One less subscription. One more connected workspace.",
    value: "$0",
    unit: "additional per user",
  },
  /** The four figures come from the brief's own reported numbers, presented as reported. */
  proof: {
    label: "Reported on the QuikSheets product page",
    stats: [
      { value: "10K+", text: "spreadsheets created" },
      { value: "2.5K+", text: "active teams" },
      { value: "50K+", text: "data records managed" },
      { value: "$0", text: "additional cost with Quikit" },
    ],
    note: "Product-reported adoption figures. Results and usage vary by organization.",
  },
};

/* ------------------------------------------------------------------ */
/*  Plans                                                              */
/* ------------------------------------------------------------------ */
/** Every figure here comes from the brief — the trial, $0 included, the $1 add-on and Enterprise. */
export const QSH_PLANS = {
  eyebrow: "Pricing",
  title: "Your spreadsheet workspace. Included with Quikit.",
  text: "No separate spreadsheet subscription. No additional per-user fee.",
  plans: [
    {
      name: "Free",
      price: "$0",
      unit: "for 14 days",
      text: "Experience the full QuikSheets product before you commit, with every feature unlocked.",
      lead: "",
      cta: "Start free trial",
      ctaHref: LINKS.register,
      foot: "No credit card required",
      features: [
        "Every QuikSheets feature unlocked",
        "Spreadsheet creation",
        "Formulas & functions",
        "Tables & data organization",
        "Charts & visualization",
        "Collaboration",
        "Comments & feedback",
        "Templates",
        "Data management",
      ],
    },
    {
      name: "Premium",
      price: "$0",
      unit: "included with Quikit",
      text: "Included with any Quikit product. No separate spreadsheet subscription.",
      lead: "Everything in Free, plus:",
      featured: true,
      badge: "Included",
      cta: "Get QuikSheets",
      ctaHref: LINKS.register,
      foot: "No separate spreadsheet licence",
      features: [
        "Full spreadsheet workspace",
        "Connections with other Quikit products",
        "Available across your organization",
        "Full REST API",
        "Email & chat support",
      ],
    },
    {
      name: "Pro",
      price: "$1",
      unit: "/ product / user / month",
      text: "Expand your Quikit workspace with additional business applications.",
      lead: "Everything in Premium, plus:",
      cta: "Explore Quikit",
      ctaHref: "/#pricing",
      foot: "Add products as your business grows",
      features: [
        "Add products as your business grows",
        "One login across the suite",
        "Shared data",
        "One AI layer",
        "QuikCRM, QuikProjects, QuikFinance and more",
      ],
    },
    {
      name: "Enterprise",
      price: "Custom",
      unit: "",
      text: "For larger organizations and bespoke requirements.",
      lead: "",
      cta: "Talk to sales",
      ctaHref: "#demo",
      foot: "Tailored to your organization",
      features: [
        "Dedicated account manager",
        "Migration and onboarding support",
        "Custom deployment",
        "Priority support",
        "API and integration support",
      ],
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  FAQ                                                                */
/* ------------------------------------------------------------------ */
export const QSH_FAQ = [
  {
    q: "What is QuikSheets?",
    a: "QuikSheets is an online spreadsheet and data management workspace within the Quikit ecosystem. It helps teams create, organize, calculate, analyze and collaborate on business data.",
  },
  {
    q: "Is QuikSheets free?",
    a: "Yes. QuikSheets is included at no additional cost with any Quikit product.",
  },
  {
    q: "Do I need a separate subscription?",
    a: "No. QuikSheets is included with your Quikit product.",
  },
  {
    q: "Is QuikSheets similar to Google Sheets?",
    a: "QuikSheets provides an online spreadsheet experience for creating, organizing, calculating and collaborating on data, while also being part of the wider Quikit business ecosystem.",
  },
  {
    q: "Is QuikSheets similar to Microsoft Excel?",
    a: "QuikSheets provides a spreadsheet experience designed for business data management and analysis. Exact feature parity with Excel depends on the capabilities currently available in QuikSheets.",
  },
  {
    q: "What can I create with QuikSheets?",
    a: "Budgets, trackers, reports, dashboards, sales sheets, project trackers, employee records, marketing reports and other business spreadsheets.",
  },
  {
    q: "Can teams collaborate on QuikSheets?",
    a: "Yes. QuikSheets is designed to support collaborative spreadsheet workflows.",
  },
  {
    q: "Does QuikSheets have formulas?",
    a: "Yes. QuikSheets supports formulas and functions for calculations and data work.",
  },
  {
    q: "Can I create charts?",
    a: "Yes. Charts and visualization capabilities turn spreadsheet data into visual insights.",
  },
  {
    q: "Can I use templates?",
    a: "Yes. QuikSheets supports reusable spreadsheet templates for common business needs.",
  },
  {
    q: "Who can use QuikSheets?",
    a: "Anyone working with business data — employees, managers, finance, sales, marketing, HR and leadership teams.",
  },
  {
    q: "Can QuikSheets connect with other Quikit products?",
    a: "Yes. QuikSheets is part of the wider Quikit ecosystem and works alongside other Quikit applications.",
  },
  {
    q: "Why use QuikSheets instead of another spreadsheet tool?",
    a: "The combination of spreadsheet functionality with the wider Quikit ecosystem, while being included with your Quikit product rather than requiring another separate subscription.",
  },
  {
    q: "Can I use QuikSheets without other Quikit products?",
    a: "QuikSheets is positioned as a spreadsheet workspace included with a Quikit product, listed at $0 rather than as a standalone paid product.",
  },
];

/* ------------------------------------------------------------------ */
/*  Demo                                                               */
/* ------------------------------------------------------------------ */
export type QshIntent = "consolidate" | "connect" | "evaluate";
export const QSH_DEMO = {
  agenda: [
    { title: "Your sheet, in context", text: "A real tracker sitting beside the CRM or project data behind it." },
    { title: "The version problem, gone", text: "One sheet, several editors, no reconciliation at the end of it." },
    { title: "The subscription you drop", text: "What your spreadsheet tool costs today against $0 with Quikit." },
  ],
  intents: [
    { value: "consolidate" as QshIntent, label: "Drop our sheets tool", meta: "We pay for one today" },
    { value: "connect" as QshIntent, label: "Connect our data", meta: "Apart from our systems" },
    { value: "evaluate" as QshIntent, label: "Just evaluating", meta: "Exploring options" },
  ],
  copy: {
    consolidate: {
      eyebrow: "Drop the extra subscription",
      title: "Let’s replace your spreadsheet tool.",
      lead: "Tell us what your team uses today and how many people are on it. We’ll show you what QuikSheets covers at no additional cost.",
      button: "Show me the comparison",
      ok: "Request received",
      msg: "A QuikSheets specialist will contact you with the comparison.",
    },
    connect: {
      eyebrow: "Connect your data",
      title: "Let’s get your sheets talking to your systems.",
      lead: "Tell us which trackers matter most and where their data comes from. We’ll show you how QuikSheets would connect them.",
      button: "Connect my data",
      ok: "Request received",
      msg: "A QuikSheets specialist will contact you about connecting your data.",
    },
    evaluate: {
      eyebrow: "Evaluate QuikSheets",
      title: "Comparing spreadsheet options?",
      lead: "Tell us how your team works with data today — a spreadsheet suite, exported reports, or a BI tool. We’ll show you how QuikSheets would fit.",
      button: "Book my walkthrough",
      ok: "Request received",
      msg: "A QuikSheets specialist will contact you to set up your walkthrough.",
    },
  } satisfies Record<QshIntent, { eyebrow: string; title: string; lead: string; button: string; ok: string; msg: string }>,
  sizes: ["1–10", "11–50", "51–200", "201–500", "500+"],
  current: ["Google Sheets", "Microsoft Excel", "Airtable", "A BI tool", "Exported reports", "Nothing formal yet"],
};

/* ------------------------------------------------------------------ */
/*  Closing                                                            */
/* ------------------------------------------------------------------ */
export const QSH_CTA = {
  eyebrow: "Your data should work as hard as your team",
  title: "Work with data\nwithout adding another tool.",
  text: "Create spreadsheets, run calculations, analyze data, build visualizations and keep it connected to the business.",
  steps: ["Create", "Calculate", "Analyze", "Visualize", "Collaborate", "Connect"],
  foot: "No extra spreadsheet subscription. Included with your Quikit product.",
};
